import {
  Button,
  makeStyles,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  tokens,
} from '@fluentui/react-components';

import { toAppError } from '@/api/errors';
import { config } from '@/app/config';

const useStyles = makeStyles({
  root: {
    margin: tokens.spacingVerticalM,
  },
  support: {
    display: 'block',
    marginTop: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground2,
  },
});

interface ErrorStateProps {
  error: unknown;
  onRetry?: (() => void) | undefined;
  title?: string;
}

/**
 * Inline error, used for failures a page can explain without losing its shell
 * and navigation. Rendered as a MessageBar, which carries role="alert" so
 * screen readers announce it when it appears.
 */
export function ErrorState({ error, onRetry, title }: ErrorStateProps) {
  const styles = useStyles();
  const appError = toAppError(error);

  const heading =
    title ?? (appError.kind === 'network' ? "Can't reach the server" : "Something didn't load");

  return (
    <div className={styles.root}>
      <MessageBar intent="error" politeness="assertive">
        <MessageBarBody>
          <MessageBarTitle>{heading}</MessageBarTitle>
          {appError.message}
          {appError.kind === 'server' && config.supportEmail && (
            <span className={styles.support}>
              If this keeps happening, contact{' '}
              <a href={`mailto:${config.supportEmail}`}>{config.supportEmail}</a>.
            </span>
          )}
        </MessageBarBody>

        {onRetry && (
          <MessageBarActions>
            <Button appearance="secondary" size="small" onClick={onRetry}>
              Try again
            </Button>
          </MessageBarActions>
        )}
      </MessageBar>
    </div>
  );
}
